import React from "react"

// Models
import {Beat} from "./model"

import AnnotationService from "./service/AnnotationService"
import {AnnotationDto, fromDto} from "./remote"

/**
 * Where the beat labels come from: the annotation endpoint, by ECG id.
 *
 * THIS IS A SEAM, ON PURPOSE.
 *
 * The marks are produced elsewhere — the prediction CSVs are imported on the
 * server, global per beat or per lead — and this screen's job is to let a
 * technician correct them and hand them back. Nothing here looks at the
 * signal.
 */

export type LabelsStatus =
    /** no record, or its sample rate is not known yet */
    | "idle"
    | "loading"
    | "ready"
    | "failed"
    /** the server counts the samples at another rate than the signal's */
    | "rate-mismatch"

export type LabelsState = {
    beats: Beat[]
    status: LabelsStatus
    /** The rate the server says its samples are at, when it says. */
    serverRate: number | null
}

const IDLE: LabelsState = {beats: [], status: "idle", serverRate: null};
const LOADING: LabelsState = {beats: [], status: "loading", serverRate: null};

type Fetch = (ecgId: string) => {subscribe(next: (response: any) => void, error: () => void): {unsubscribe(): void}};

/**
 * Labels for a record, fetched whenever the ECG or its rate changes.
 *
 * Every result is kept with the ECG id and rate it was fetched for, and only
 * returned while those are still the current ones. Without that, the render
 * right after switching records handed back the PREVIOUS record's ready beats
 * under the new id — and they could be latched and saved there.
 *
 * Nothing is fetched until the rate is known: samples are converted to ms
 * with it, and a guessed rate converts every mark wrongly.
 *
 * @param ecgId
 * @param sampleRate the record's own rate, or null while not known
 * @param fetch for tests; the annotation endpoint by default
 */
export default function useRecordLabels(
    ecgId: string | null,
    sampleRate: number | null,
    fetch?: Fetch,
): LabelsState {
    const key = ecgId && sampleRate ? `${ecgId}@${sampleRate}` : null;
    const [state, setState] = React.useState<{key: string | null, labels: LabelsState}>({key: null, labels: IDLE});
    const fetchRef = React.useRef<Fetch>(fetch || (id => AnnotationService.fetchAnnotation(id)));

    React.useEffect(() => {
        if (!key || !ecgId || !sampleRate) {
            return
        }

        const subscription = fetchRef.current(ecgId).subscribe(
            response => {
                const dto: AnnotationDto = response && response.response && response.response.data
                    ? response.response.data
                    : {beats: [], leads: []};
                const serverRate = typeof dto.sampleRate === "number" ? dto.sampleRate : null;

                /* positions counted at another rate are not ours to convert */
                if (serverRate !== null && serverRate !== sampleRate) {
                    setState({key: key, labels: {beats: [], status: "rate-mismatch", serverRate: serverRate}});

                    return
                }

                setState({key: key, labels: {beats: fromDto(dto, sampleRate), status: "ready", serverRate: serverRate}})
            },
            () => setState({key: key, labels: {beats: [], status: "failed", serverRate: null}}),
        );

        /* a record left before its labels arrive must not receive them */
        return () => subscription.unsubscribe()
    }, [key, ecgId, sampleRate]);

    if (!key) {
        return IDLE
    }

    return state.key === key ? state.labels : LOADING
}
